import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSkeleton } from "../../../hooks/useSkeleton";

import {
    Button,
    MuiThemeProvider,
    TextField,
    createMuiTheme,
} from "@material-ui/core";
import {
    Card,
    CardBody,
    CardHeader
} from "../../../../_metronic/_partials/controls";
import { deleteWebProject, getWebProjectById, postWebProject, updateWebProject } from "../../../../api/webProject";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import LogoSection from "../../../components/UploadLogo";


// Create theme for delete button (red)
const theme = createMuiTheme({
    palette: {
        secondary: {
            main: "#F64E60",
        },
    },
});

function getEmptyWebProject() {
    return {
        title: "",
        logoURL: "",
        location: "",
        sizeMW: null, // Number
        status: "",
        order: null
    }
}


const DefaultTextField = ({ label, value, onChange, required = true, ...props }) => {

    return (
        <TextField
            label={label}
            value={value}
            onChange={onChange}
            fullWidth
            margin="normal"
            variant="outlined"
            required={required}
            {...props}
        />
    );
};

export default function EditWebProjectPage() {
    const history = useHistory();
    const {
        isLoading: isLoadingData,
        disableLoading: disableLoadingData,
        ContentSkeleton
    } = useSkeleton();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const webProjectId = useParams().id;
    const [webProject, setWebProject] = useState(getEmptyWebProject());
    const [selectedLogo, setSelectedLogo] = useState(null);
    const [openPreviewDialog, setOpenPreviewDialog] = useState(null);

    useEffect(() => {
        if (!webProjectId) {
            disableLoadingData();
            return;
        }

        getWebProjectById(webProjectId)
            .then((res) => {
                setWebProject(res.data);
                disableLoadingData();
            })
            .catch((error) => {
                alertError({ error: error, customMessage: "Could not get web project." });
                disableLoadingData();
            });
    }, [webProjectId, disableLoadingData, history]);


    const handleChange = (field) => (event) => {
        setWebProject({ ...webProject, [field]: event.target.value });
    };

    function saveWebProject() {
        if (webProjectId) {
            updateWebProject(webProjectId, webProject, selectedLogo).then(res => {
                alertSuccess({ title: 'Saved!', customMessage: 'Changes successfully saved.' })
                history.push("/webProjects")
            }).catch(error => {
                alertError({ error: error, customMessage: 'Could not save changes.' })
            })
        }
        else {
            postWebProject(webProject, selectedLogo)
                .then((res) => {
                    alertSuccess({ title: "Saved!", customMessage: "Web project successfully created." });
                    history.push("/webProjects");
                })
                .catch((error) => {
                    alertError({ error: error, customMessage: "Could not save the web project." });
                });
        }
    }

    if (isLoadingData)
        return <ContentSkeleton />;

    return (
        <>
            <Card>
                <CardHeader title="Create new web project" />
                <CardBody>
                    <div className="d-flex align-item-center">
                        <div className="col-4">
                            <DefaultTextField label={"Title"} value={webProject.title} onChange={handleChange("title")} />
                        </div>
                        <div className="col-4">
                            <DefaultTextField label={"Location"} value={webProject.location} onChange={handleChange("location")} />
                        </div>

                        <div className="col-4">
                            <LogoSection
                                selectedLogo={selectedLogo}
                                setSelectedLogo={setSelectedLogo}
                                objectClass={webProject}
                                setObjectClass={setWebProject}
                                openPreviewDialog={openPreviewDialog}
                                setOpenPreviewDialog={setOpenPreviewDialog}
                            />
                        </div>

                    </div>
                    <div className="d-flex align-item-center">
                        <div className="col-2">
                            <DefaultTextField label={"Status"} value={webProject.status} onChange={handleChange("status")} />
                        </div>

                        <div className="col-2">
                            <DefaultTextField
                                label="SizeMW"
                                value={webProject.sizeMW}
                                onChange={handleChange("sizeMW")}
                                required={false}
                            />
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Button
                onClick={() => history.push("/webProjects")}
                variant="outlined"
                style={{ marginRight: 20 }}
            >
                Back
            </Button>

            <Button
                onClick={() => saveWebProject()}
                variant="outlined"
                color="primary"
                style={{ marginRight: 20 }}
            >
                Save Web Project
            </Button>
            {
                webProjectId && (
                    <>
                        <MuiThemeProvider theme={theme}>
                            <Button
                                onClick={() => setOpenConfirmDialog(true)}
                                variant="outlined"
                                color="secondary"
                            >
                                Delete Web Project
                            </Button>
                        </MuiThemeProvider>

                        <ConfirmDialog
                            title="Are you sure you want to delete this web project?"
                            open={openConfirmDialog}
                            setOpen={setOpenConfirmDialog}
                            onConfirm={() => {
                                deleteWebProject(webProjectId)
                                    .then((res) => {
                                        alertSuccess({
                                            title: "Deleted!",
                                            customMessage: "Web project deleted successfully",
                                        });
                                        history.push("/webProjects");
                                    })
                                    .catch((error) => {
                                        alertError({
                                            error: error,
                                            customMessage: "Could not delete web project.",
                                        });
                                    });
                            }}
                        />
                    </>
                )
            }
        </>
    );
}